import { COOKIE_NAMES, STORE_NAME } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { container, UserInfo } from '@intigral/domain';
import { IAuthService } from '@intigral/domain/lib/application/services/AuthService';
import { ISubscriptionService } from '@intigral/domain/lib/application/services/SubscriptionService.js';
import { JsonType } from '@intigral/types';
import { getCookie, log } from '@intigral/utils';
import { get, remove, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import userActions from '../actions/users';
import { addUser } from '../store/reducers/userSlice';
import { getUserInfo, persistSessionUser, persistUserWithSubscription, storeUserInfo } from './user';
import {
    isGuestUser,
    buildGuestUserFromSessionCookie,
    onGuestUserSession,
    onSignedInUserSession,
    mergeSignedInUserSession,
    clearWcpClientAuthState,
    clearAuthSessionCookies,
    isSignedInSessionCookie,
    clearStaleSignedInCookieWithoutLocalUser,
    consumeGuestExploreIntent,
    captureGuestExploreFromUrl,
    isGuestExplorePending,
    isInvalidPersistedUser,
    isGuestBrowseProfile,
    type GuestSessionCookie,
} from '../guest';
import { appConfig } from '../config/app.config';

type UserData = JsonType;
type LoginTokenData = GuestSessionCookie;

interface RefreshTokenError {
    refreshTokenError: true;
    code: string;
}

const FIVE_MINUTES_MS = 60 * 5 * 1000;
const REFRESH_CHECK_INTERVAL_MS = FIVE_MINUTES_MS;

const refreshAuthToken = async (tokenData: LoginTokenData) => {
    if (tokenData?.isGuest === true) {
        throw new Error('Auth token refresh is not allowed for guest browse sessions');
    }
    const authService = container.resolve<IAuthService>(DI.SERVICES.IAuthService);
    return authService.refreshAuthToken(tokenData.u, tokenData.t, tokenData.i, tokenData.s);
};

const fetchSubscriptionStatus = async (authUser: UserInfo): Promise<Record<string, unknown>> => {
    try {
        const subscriptionService = container.resolve<ISubscriptionService>(DI.SERVICES.ISubscriptionService);
        const subscription = await subscriptionService.getSubscriptionStatus(
            authUser.apiKey,
            authUser.username,
            authUser.authToken,
            authUser.identityToken
        );
        return subscription as unknown as Record<string, unknown>;
    } catch (error) {
        return {};
    }
};

const mergeAuthIntoStoredUser = (authUserData: Record<string, unknown>): UserInfo => {
    const currentUserInfo = getUserInfo() || {};
    return { ...currentUserInfo, ...authUserData, createdTime: Date.now() } as UserInfo;
};

type GenerateNewTokenResult = { result: 'new-token'; params: LoginTokenData };
type UserDataExistResult = { result: 'user-exists'; params: UserInfo | null };
type NoUserResult = { result: 'no-user'; params: null };
type InitialTokenResult = GenerateNewTokenResult | UserDataExistResult | NoUserResult;

function hadGuestBrowseContext(): boolean {
    return isGuestUser(getUserInfo()) || isGuestBrowseProfile(get(STORE_NAME.SELECTED_PROFILE));
}

export const loginUserWithToken = async (
    tokenData: LoginTokenData
): Promise<UserData | RefreshTokenError | Record<string, unknown>> => {
    if (tokenData?.isGuest === true) {
        const guest = buildGuestUserFromSessionCookie(tokenData);
        persistSessionUser(guest, { replace: true });
        onGuestUserSession();
        return guest as unknown as UserData;
    }

    try {
        const userData = await refreshAuthToken(tokenData);

        if (!userData?.username) {
            throw new Error('User object not returned successfully from refresh token API');
        }

        const updatedUserData = { ...userData, createdTime: Date.now() };
        const userStoredData = getUserInfo();
        const clearProfile = hadGuestBrowseContext();
        const mergedUser = mergeSignedInUserSession(userStoredData, updatedUserData);
        onSignedInUserSession(mergedUser, { clearProfile });
        persistSessionUser(mergedUser, { replace: true });

        if (!updatedUserData.apiKey) {
            throw new Error('Invalid user API key on login');
        }

        return mergedUser;
    } catch (err) {
        const errorCode = err?.data?.code || err?.data?.status || 'REFRESH_TOKEN_ERROR';
        return { refreshTokenError: true, code: errorCode };
    }
};

export const useUserSubscriptionData = (enabled: boolean) =>
    useQuery({
        enabled,
        queryKey: ['user-subscription'],
        queryFn: async () => {
            try {
                const userInfo = getUserInfo();
                const subscription = await fetchSubscriptionStatus(userInfo);
                persistUserWithSubscription(userInfo, subscription);
                return { subscriptionStatus: subscription };
            } catch (ex) {
                return { subscriptionStatus: false };
            }
        },
    });

export function bootstrapAuthSession(): {
    action: InitialTokenResult;
    initialUser: UserInfo | null;
} {
    captureGuestExploreFromUrl();
    consumeGuestExploreIntent();
    clearStaleSignedInCookieWithoutLocalUser();
    const action = getInitialAuthAction();

    if (action.result !== 'user-exists' || !action.params) {
        return { action, initialUser: null };
    }

    if (isGuestUser(action.params)) {
        const currentLocal = getUserInfo();
        if (currentLocal?.username && !isGuestUser(currentLocal)) {
            const clearProfile = hadGuestBrowseContext();
            const signedIn = mergeSignedInUserSession(null, currentLocal);
            onSignedInUserSession(signedIn, { clearProfile });
            persistSessionUser(signedIn, { replace: true });
            return { action, initialUser: signedIn };
        }

        persistSessionUser(action.params as unknown as Record<string, unknown>, { replace: true });
        onGuestUserSession();
        return { action, initialUser: action.params };
    }

    const clearProfile = hadGuestBrowseContext();
    const signedIn = mergeSignedInUserSession(getUserInfo(), action.params);
    onSignedInUserSession(signedIn, { clearProfile });
    persistSessionUser(signedIn, { replace: true });
    return { action, initialUser: signedIn };
}

export const useAuthToken = (enabled: boolean) => {
    const dispatch = useDispatch();
    const bootstrapRef = useRef<ReturnType<typeof bootstrapAuthSession> | null>(null);
    if (bootstrapRef.current === null) {
        bootstrapRef.current = bootstrapAuthSession();
    }
    const { action: initialAction, initialUser } = bootstrapRef.current;

    const [userData, setUserData] = useState<UserInfo | null>(initialUser);
    const needsSignedInCookieHydration =
        !isGuestExplorePending() &&
        initialAction.result === 'new-token' &&
        initialAction.params !== null &&
        initialAction.params?.isGuest !== true;
    const [isHydratingFromCookie, setIsHydratingFromCookie] = useState(needsSignedInCookieHydration);

    useEffect(() => {
        if (initialUser) {
            dispatch(addUser(initialUser));
        }
    }, [dispatch, initialUser]);

    const {
        mutateAsync: refreshTokenAsync,
        isError,
        error,
    } = useMutation({
        mutationFn: async (tokenData: LoginTokenData) => {
            if (isGuestExplorePending() && tokenData?.isGuest !== true) {
                throw new Error('Signed-in refresh blocked during guest explore');
            }
            if (tokenData?.isGuest === true) {
                return { user: buildGuestUserFromSessionCookie(tokenData), subscription: {} };
            }
            set(STORE_NAME.REFRESH_TOKEN_API_IN_PROGRESS, 'true');
            const authUserData = await refreshAuthToken(tokenData);

            if (!authUserData.authToken) {
                throw new Error(
                    'Invalid used data returned while refreshing token. Data:' + JSON.stringify(authUserData)
                );
            }

            const userWithAuth = mergeAuthIntoStoredUser(authUserData as unknown as Record<string, unknown>);
            const subscription = await fetchSubscriptionStatus(userWithAuth);
            return { user: userWithAuth, subscription };
        },
        onSuccess: ({ user: userInfo, subscription }) => {
            if (isGuestUser(userInfo)) {
                persistSessionUser(userInfo, { replace: true });
                dispatch(addUser(userInfo));
                setUserData(userInfo);
                onGuestUserSession();
                return;
            }

            const clearProfile = hadGuestBrowseContext();
            const signedInUser = mergeSignedInUserSession(getUserInfo(), userInfo);
            onSignedInUserSession(signedInUser, { clearProfile });
            const persistedUser = persistUserWithSubscription(signedInUser, subscription);
            dispatch(addUser(persistedUser));
            setUserData(persistedUser);
        },
        onError: (refreshError, variables) => {
            if (variables?.isGuest === true) {
                return;
            }
            userActions.logoutUser();
        },
    });

    const { data: tokenStatus } = useQuery({
        queryKey: ['refresh-token-status'],
        /** Guest sessions use locate-style credentials; refreshAuthToken is for full logins only. */
        enabled: enabled && userData !== null && !isGuestUser(userData),
        refetchInterval: REFRESH_CHECK_INTERVAL_MS,
        staleTime: Infinity,
        refetchIntervalInBackground: true,
        queryFn: async () => {
            const userInfo = getUserInfo();
            const expiresAt = userInfo.createdTime + userInfo.duration;
            const remainingTime = expiresAt - Date.now();
            const isExpiring = remainingTime <= FIVE_MINUTES_MS;

            if (!isExpiring) {
                return { isExpiring };
            }

            await refreshTokenAsync({
                u: userInfo.username,
                t: userInfo.authToken,
                i: userInfo.identityToken,
                s: userInfo.authSign,
            });
            return { isExpiring };
        },
    });

    useEffect(() => {
        if (!enabled) {
            return;
        }

        if (initialAction.result === 'new-token' && initialAction.params && initialAction.params?.isGuest === true) {
            const guest = buildGuestUserFromSessionCookie(initialAction.params);
            persistSessionUser(guest, { replace: true });
            setUserData(guest);
            dispatch(addUser(guest));
            onGuestUserSession();
            setIsHydratingFromCookie(false);
        }
    }, [enabled, initialAction, dispatch]);

    useEffect(() => {
        if (!enabled || !needsSignedInCookieHydration || !isHydratingFromCookie || !initialAction.params) {
            return;
        }

        if (isGuestExplorePending()) {
            setIsHydratingFromCookie(false);
            return;
        }

        let cancelled = false;

        (async () => {
            const result = await loginUserWithToken(initialAction.params!);
            if (cancelled) {
                return;
            }

            if (result && !('refreshTokenError' in result && result.refreshTokenError)) {
                const user = result as unknown as UserInfo;
                dispatch(addUser(user));
                setUserData(user);
            } else {
                userActions.logoutUser();
            }

            setIsHydratingFromCookie(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [enabled, needsSignedInCookieHydration, isHydratingFromCookie, initialAction, dispatch]);

    return {
        isRefreshing: tokenStatus,
        refreshFailed: isError,
        refreshError: error,
        user: userData,
        isHydratingFromCookie,
        authBootstrapAction: initialAction,
    };
};

const loginTokenFromCookie = (): LoginTokenData | null => {
    const userCookieData = getCookie(COOKIE_NAMES.USER_SESSION);
    if (!userCookieData) {
        return null;
    }
    return JSON.parse(window.atob(decodeURIComponent(userCookieData)));
};

/**
 * Determines the initial authentication action based on the presence and validity
 * of login cookies and local user data.
 */
const getInitialAuthAction = (): InitialTokenResult => {
    if (isGuestExplorePending()) {
        clearWcpClientAuthState();
        return { result: 'no-user', params: null };
    }

    const cookieLoginToken = loginTokenFromCookie();
    let localUserData = getUserInfo();

    if (isInvalidPersistedUser(localUserData)) {
        remove(STORE_NAME.USER);
        localUserData = null;
    }

    if (!localUserData && cookieLoginToken === null) {
        return { result: 'no-user', params: null };
    }

    if (localUserData && !localUserData.createdTime) {
        localUserData = { ...localUserData, createdTime: Date.now() };
    }

    if (cookieLoginToken === null) {
        return { result: 'user-exists', params: localUserData };
    }

    if (!localUserData) {
        if (cookieLoginToken?.isGuest === true) {
            const guest = buildGuestUserFromSessionCookie(cookieLoginToken);
            persistSessionUser(guest, { replace: true });
            return { result: 'user-exists', params: guest };
        }
        return { result: 'new-token', params: cookieLoginToken };
    }

    const { username, authToken } = localUserData;
    const { t: cookieToken, u: cookieUser } = cookieLoginToken;
    const cookieIsGuest = cookieLoginToken?.isGuest === true;
    const localIsGuest = isGuestUser(localUserData);
    const cookieIsSignedIn = isSignedInSessionCookie(cookieLoginToken);
    const tokensMatch =
        username.toLowerCase() === cookieUser.toLowerCase() && cookieToken.toLowerCase() === authToken.toLowerCase();

    if (tokensMatch) {
        return { result: 'user-exists', params: localUserData };
    }

    if (!localIsGuest && cookieIsGuest) {
        onSignedInUserSession(localUserData, { clearProfile: true });
        storeUserInfo(localUserData, appConfig.userCookieExpirationDays ?? 30);
        return { result: 'user-exists', params: localUserData };
    }

    if (localIsGuest && cookieIsSignedIn) {
        return { result: 'new-token', params: cookieLoginToken };
    }

    if (cookieIsGuest) {
        const guest = buildGuestUserFromSessionCookie(cookieLoginToken);
        persistSessionUser(guest, { replace: true });
        return { result: 'user-exists', params: guest };
    }

    if (localIsGuest) {
        clearWcpClientAuthState();
    }

    return { result: 'new-token', params: cookieLoginToken };
};
